import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MoreHorizontal, Pencil, Plus, Send } from "lucide-react";
import { api } from "../../api";
import { useApi } from "../../hooks";
import { useAuth } from "../../auth";
import { contactName, shortWhen, stageLabel } from "../../crm";
import { confirmLeave, setLeaveGuard } from "../../leave-guard";
import {
  BLANK_CONTACT, DRIP_STATUS_LABEL, MAX_STEPS, SAMPLE_CONTACT,
  mergeFields, previewParagraphs, readableFields, waitText, whoSentence,
  type Drip, type DripPerson, type DripStep,
} from "../../drips";
import { ErrorState, LoadingState, PageHeader } from "../Ui";
import Dialog from "../Dialog";
import QuickMenu, { QuickMenuItem } from "../QuickMenu";
import ContactDrawer from "./ContactDrawer";
import DripSettingsDialog from "./DripSettingsDialog";
import type { ContactRow, CrmForm } from "./types";
import "../../polish/crm-drips.css";

type Loaded = Drip & { active?: number };
/** The email open for editing: its place in the list (a new one goes at the end) and its fields. */
type Editing = { index: number; isNew: boolean; step: DripStep; saved: string };

const MERGE_BUTTONS = [["first_name", "First name"], ["last_name", "Last name"], ["company", "Company"], ["email", "Email"]] as const;
const stepBody = (s: DripStep) => ({ id: s.id ?? null, wait_days: s.wait_days, subject: s.subject, body: s.body });

/** The one line shown while drip email waits on the No Limits sending domain. */
export function DomainWait() {
  return (
    <div className="drip-strip is-waiting" role="status">
      <span className="drip-dot" aria-hidden="true" />
      <span>Sending waits on the No Limits email domain</span>
      <Link to="/settings#integrations/password_email">Email in Settings</Link>
    </div>
  );
}

/**
 * One drip on one page, laid out like a Magus campaign: status line, the numbers, who
 * gets it, the emails (each opens in place with a live preview), and the people in it.
 * Name, sender, start rules and mailing address are in Edit drip.
 */
export default function DripBuilder({ id, onBack }: { id: number; onBack: () => void }) {
  const loaded = useApi<Loaded>(`/api/crm/drips/${id}`);
  const forms = useApi<CrmForm[]>("/api/crm/forms");
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [blankPreview, setBlankPreview] = useState(false);
  const [goLive, setGoLive] = useState<{ include: boolean } | null>(null);
  const drip = loaded.data;

  // A drip made with New drip opens with its first email ready to write (?edit=0).
  const openedFirst = useRef(false);
  useEffect(() => {
    if (!drip || openedFirst.current) return;
    openedFirst.current = true;
    const want = params.get("edit");
    if (want !== null && drip.steps[Number(want)] && canWrite) startEdit(Number(want));
    const started = params.get("started");
    if (started !== null) {
      const n = Number(started) || 0;
      const names = [...new Set(drip.triggers.filter((t) => t.type === "stage").map((t) => stageLabel((t as { stage: string }).stage)))].join(" or ");
      setNotice({ ok: true, text: `The drip is live. Emails go out between 9:00 AM and 5:00 PM ET.${n ? ` Started for ${n} ${n === 1 ? "person" : "people"} already in ${names}.` : ""}` });
    }
    if (want !== null || started !== null) setParams((cur) => { const n = new URLSearchParams(cur); n.delete("edit"); n.delete("started"); return n; }, { replace: true });
  }, [drip]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = editing ? JSON.stringify(stepBody(editing.step)) !== editing.saved || editing.isNew : false;
  useEffect(() => {
    if (!dirty) return;
    setLeaveGuard("This email has changes that are not saved. Leave without saving?");
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => { setLeaveGuard(null); window.removeEventListener("beforeunload", warn); };
  }, [dirty]);

  if (loaded.loading && !drip) return <LoadingState label="Loading drip" />;
  if (loaded.error || !drip) return <ErrorState message={loaded.error || "This drip could not load."} onRetry={loaded.reload} />;

  const d = drip;
  const counts = d.counts || { people: 0, active: d.active || 0, finished: 0, stopped: 0, sent: 0 };
  const formName = (fid: number) => forms.data?.find((f) => f.id === fid)?.name || "a form";

  function startEdit(index: number) {
    if (dirty && !window.confirm("Leave the email you are editing without saving it?")) return;
    const step = d.steps[index];
    setNotice(null);
    setEditing({ index, isNew: false, step: { ...step }, saved: JSON.stringify(stepBody(step)) });
  }
  function addEmail() {
    if (dirty && !window.confirm("Leave the email you are editing without saving it?")) return;
    const step = { id: null, wait_days: d.steps.length ? 2 : 0, subject: "", body: "" };
    setNotice(null);
    setEditing({ index: d.steps.length, isNew: true, step, saved: JSON.stringify(stepBody(step)) });
  }
  /** One save for the whole drip (the API's shape): its settings as they are, these emails. */
  async function saveSteps(steps: DripStep[], done: string): Promise<boolean> {
    setBusy(true); setNotice(null);
    try {
      await api(`/api/crm/drips/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: d.name, from_name: d.from_name, reply_to: d.reply_to, footer_address: d.footer_address,
          triggers: d.triggers, stop_on_booking: d.stop_on_booking, steps: steps.map(stepBody),
        }),
      });
      loaded.reload();
      if (done) setNotice({ ok: true, text: done });
      return true;
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "The drip did not save." }); return false; }
    finally { setBusy(false); }
  }
  async function saveEmail() {
    if (!editing) return;
    const steps = editing.isNew ? [...d.steps, editing.step] : d.steps.map((s, i) => (i === editing.index ? editing.step : s));
    if (await saveSteps(steps, `Email ${editing.index + 1} saved.`)) setEditing(null);
  }
  function move(i: number, by: -1 | 1) {
    const steps = [...d.steps];
    [steps[i], steps[i + by]] = [steps[i + by], steps[i]];
    void saveSteps(steps, "");
  }
  function removeEmail(i: number) {
    if (!window.confirm(`Delete email ${i + 1}? People who already got it keep it.`)) return;
    void saveSteps(d.steps.filter((_, k) => k !== i), `Email ${i + 1} deleted.`);
  }
  async function setStatus(status: "live" | "paused", includeCurrent = false) {
    // Going live asks first when someone would get an email right away: people in it, or
    // people already in its start stages (who a stage rule alone would never start).
    if (status === "live" && !goLive && (counts.active > 0 || (d.in_stage_now ?? 0) > 0)) { setGoLive({ include: true }); return; }
    setGoLive(null);
    setBusy(true); setNotice(null);
    try {
      const out = await api<Drip>(`/api/crm/drips/${id}/status`, { method: "POST", body: JSON.stringify({ status, include_current: includeCurrent }) });
      setNotice({ ok: true, text: status === "live" ? liveText(out.started || 0) : "Paused. Nobody gets an email until you resume it." });
      loaded.reload();
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "That did not work." }); }
    finally { setBusy(false); }
  }
  async function sendTest(position: number) {
    if (editing && !editing.isNew && editing.index === position && dirty && !(await saveSteps(d.steps.map((s, i) => (i === position ? editing.step : s)), ""))) return;
    setBusy(true); setNotice(null);
    try {
      const out = await api<{ to: string }>(`/api/crm/drips/${id}/test`, { method: "POST", body: JSON.stringify({ position }) });
      setNotice({ ok: true, text: `Email ${position + 1} sent to ${out.to} as a test.` });
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "The test email did not send." }); }
    finally { setBusy(false); }
  }
  async function removeDrip() {
    if (!window.confirm(`Delete "${d.name}"? People in it stop getting its emails. What was already sent stays on each contact's timeline.`)) return;
    setBusy(true);
    try { await api(`/api/crm/drips/${id}`, { method: "DELETE" }); setLeaveGuard(null); onBack(); }
    catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "The drip was not deleted." }); setBusy(false); }
  }

  const inStageNames = [...new Set(d.triggers.filter((t) => t.type === "stage").map((t) => stageLabel((t as { stage: string }).stage)))].join(" or ");
  const liveText = (started: number) => `The drip is live. Emails go out between 9:00 AM and 5:00 PM ET.${started ? ` Started for ${started} ${started === 1 ? "person" : "people"} already in ${inStageNames}.` : ""}`;

  const status = d.status;
  const statusAction = status === "live" ? "Pause" : status === "paused" ? "Resume" : "Go live";
  const sender = d.from_name || "No sender name yet";
  const emailCards = editing?.isNew ? [...d.steps, editing.step] : d.steps;

  return (
    <div className="crm-drips drip-page">
      <button type="button" className="drip-back" onClick={() => { if (confirmLeave()) onBack(); }}><ArrowLeft aria-hidden="true" />All drips</button>
      <PageHeader title={d.name || "Untitled drip"}
        description={`${whoSentence(d.triggers, formName, stageLabel)}${d.stop_on_booking ? " Stops when they book a call." : ""}`}
        actions={canWrite && <button className="button" onClick={() => setSettingsOpen(true)}><Pencil aria-hidden="true" />Edit drip</button>} />

      <div className={`drip-strip is-${status}`}>
        <span className="drip-dot" aria-hidden="true" />
        <span>{DRIP_STATUS_LABEL[status]}. {d.from_name ? `Sends from ${d.from_name}${d.from_address ? ` (${d.from_address})` : ""}, 9:00 AM to 5:00 PM ET.` : "No sender name yet. Sends 9:00 AM to 5:00 PM ET."}</span>
        {canWrite && <button type="button" disabled={busy} onClick={() => void setStatus(status === "live" ? "paused" : "live")}>{statusAction}</button>}
      </div>
      {d.sending?.waiting_on_domain && <DomainWait />}
      {canWrite && status === "live" && (d.in_stage_now ?? 0) > 0 && (
        <p className="inline-notice drip-in-stage">
          <span>{d.in_stage_now} {d.in_stage_now === 1 ? "person is" : "people are"} already in {inStageNames} and not in this drip.</span>
          <button type="button" className="button secondary small" disabled={busy} onClick={() => void setStatus("live", true)}>Start {d.in_stage_now === 1 ? "them" : "them all"}</button>
        </p>
      )}
      {notice && <p className={`inline-notice${notice.ok ? " success" : " danger"}`} role={notice.ok ? "status" : "alert"}>{notice.text}</p>}

      <div className="drip-summary">
        {([["People", counts.people], ["Active", counts.active], ["Emails sent", counts.sent], ["Finished", counts.finished], ["Stopped", counts.stopped]] as const).map(([label, n]) => (
          <div key={label}><strong>{n}</strong><span>{label}</span></div>
        ))}
      </div>

      <div className="drip-section-heading">
        <h2>Emails</h2>
        {canWrite && d.steps.length + (editing?.isNew ? 1 : 0) < MAX_STEPS && <button className="button secondary" disabled={busy} onClick={addEmail}><Plus aria-hidden="true" />Add email</button>}
      </div>
      <ol className="drip-emails">
        {emailCards.map((s, i) => {
          const open = editing && editing.index === i;
          if (open) {
            return (
              <EmailEditor key={`edit-${i}`} index={i} drip={d} editing={editing} busy={busy} blank={blankPreview}
                onToggleBlank={() => setBlankPreview((v) => !v)}
                onChange={(step) => setEditing((cur) => (cur ? { ...cur, step } : cur))}
                onCancel={() => setEditing(null)} onSave={() => void saveEmail()}
                onTest={editing.isNew ? null : () => void sendTest(i)} />
            );
          }
          const preview = readableFields(s.body).replace(/\s+/g, " ").trim();
          return (
            <li key={s.id ?? `n${i}`} className="drip-email">
              <div className="drip-email-main">
                <div className="drip-email-meta"><span>Email {i + 1}</span><span>{waitText(s.wait_days, i)}</span></div>
                <h3>{s.subject ? readableFields(s.subject) : <span className="drip-missing">No subject yet</span>}</h3>
                <p className="drip-email-snippet">{preview || <span className="drip-missing">No message yet</span>}</p>
              </div>
              {canWrite && (
                <div className="drip-row-actions">
                  <button type="button" className="button secondary" disabled={busy} onClick={() => startEdit(i)}>Edit</button>
                  <QuickMenu label={`More for email ${i + 1}`} align="right" trigger={<span className="icon-button drip-more" title="More"><MoreHorizontal aria-hidden="true" /></span>}>
                    {(close) => (
                      <>
                        <QuickMenuItem onClick={() => { close(); void sendTest(i); }}><Send aria-hidden="true" />Send me a test</QuickMenuItem>
                        {i > 0 && <QuickMenuItem onClick={() => { close(); move(i, -1); }}>Move up</QuickMenuItem>}
                        {i < d.steps.length - 1 && <QuickMenuItem onClick={() => { close(); move(i, 1); }}>Move down</QuickMenuItem>}
                        {d.steps.length > 1 && <QuickMenuItem tone="danger" onClick={() => { close(); removeEmail(i); }}>Delete email</QuickMenuItem>}
                      </>
                    )}
                  </QuickMenu>
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <DripPeople dripId={id} status={status} total={d.steps.length} canWrite={canWrite} onChanged={() => loaded.reload()} />

      {canWrite && status !== "live" && <button type="button" className="button secondary drip-delete" disabled={busy} onClick={() => void removeDrip()}>Delete drip</button>}

      {goLive && (
        <Dialog title={status === "paused" ? "Resume this drip" : "Go live"} onClose={() => setGoLive(null)}>
          <div className="drip-form">
            <p>Emails go out between 9:00 AM and 5:00 PM ET, from {sender}.</p>
            {counts.active > 0 && <p>The {counts.active} {counts.active === 1 ? "person" : "people"} in it start getting emails.</p>}
            {(d.in_stage_now ?? 0) > 0 && (
              <label className="drip-check"><input type="checkbox" checked={goLive.include} onChange={(e) => setGoLive({ include: e.target.checked })} />
                Also start the {d.in_stage_now} {d.in_stage_now === 1 ? "person" : "people"} already in {inStageNames}</label>
            )}
            <div className="operation-actions">
              <button type="button" className="button secondary" onClick={() => setGoLive(null)}>Cancel</button>
              <button type="button" className="button" disabled={busy} onClick={() => void setStatus("live", goLive.include && (d.in_stage_now ?? 0) > 0)}>{status === "paused" ? "Resume" : "Go live"}</button>
            </div>
          </div>
        </Dialog>
      )}

      {settingsOpen && (
        <DripSettingsDialog drip={d} forms={forms.data || []} onClose={() => setSettingsOpen(false)}
          onSaved={() => { setSettingsOpen(false); setNotice({ ok: true, text: "Drip saved." }); loaded.reload(); }} />
      )}
    </div>
  );
}

/** One email open in place: when it goes, subject and message, with the lead's view beside it. */
function EmailEditor({ index, drip, editing, busy, blank, onToggleBlank, onChange, onCancel, onSave, onTest }: {
  index: number; drip: Drip; editing: Editing; busy: boolean; blank: boolean;
  onToggleBlank: () => void; onChange: (s: DripStep) => void; onCancel: () => void; onSave: () => void; onTest: (() => void) | null;
}) {
  const s = editing.step;
  const last = useRef<{ field: "subject" | "body"; el: HTMLInputElement | HTMLTextAreaElement } | null>(null);
  const track = (field: "subject" | "body") => (e: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => { last.current = { field, el: e.currentTarget }; };
  /** Puts a merge field where the cursor was in the subject or message (the message by default). */
  function insert(field: string) {
    const at = last.current;
    const key = at?.field || "body";
    const token = field === "first_name" ? "{{first_name|there}}" : `{{${field}}}`;
    const value = s[key];
    const start = at?.el.selectionStart ?? value.length;
    const end = at?.el.selectionEnd ?? value.length;
    onChange({ ...s, [key]: value.slice(0, start) + token + value.slice(end) });
    // Back to the field, unless they already moved on to another one.
    window.requestAnimationFrame(() => {
      const el = at?.el;
      const now = document.activeElement;
      if (el?.isConnected && (now === el || now === document.body || now?.closest(".drip-merge"))) { el.focus(); el.setSelectionRange(start + token.length, start + token.length); }
    });
  }
  function submit(e: FormEvent) { e.preventDefault(); onSave(); }
  return (
    <li className="drip-email is-editing">
      <form className="drip-email-form" onSubmit={submit}>
        <div className="drip-email-meta"><span>Email {index + 1}</span></div>
        <label className="drip-wait">
          <span className="sr-only">Days to wait</span>
          <input inputMode="numeric" value={String(s.wait_days)} maxLength={3} aria-label={`Email ${index + 1}: days to wait`}
            onChange={(e) => onChange({ ...s, wait_days: Math.min(365, Number(e.target.value.replace(/\D/g, "")) || 0) })} />
          <span>{s.wait_days === 1 ? "day" : "days"} after {index === 0 ? "they join" : `email ${index}`}</span>
        </label>
        <label>Subject
          <input value={s.subject} maxLength={200} placeholder="A quick idea for {{company|your business}}" autoFocus={editing.isNew}
            onChange={(e) => { onChange({ ...s, subject: e.target.value }); track("subject")(e); }} onSelect={track("subject")} onFocus={track("subject")}
            // Enter in the subject goes on to the message; it never saves the email by accident.
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.closest("form")?.querySelector("textarea")?.focus(); } }} />
        </label>
        <label>Message
          <textarea rows={10} value={s.body} maxLength={20000} placeholder={"Hi {{first_name|there}},\n\nWrite it like a note to one person. A blank line starts a new paragraph."}
            onChange={(e) => { onChange({ ...s, body: e.target.value }); track("body")(e); }} onSelect={track("body")} onFocus={track("body")} />
        </label>
        <div className="drip-merge">
          <span>Personalize</span>
          {MERGE_BUTTONS.map(([f, label]) => <button key={f} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(f)}>{label}</button>)}
        </div>
        <div className="drip-email-buttons">
          {onTest && <button type="button" className="button secondary" disabled={busy} onClick={onTest}><Send aria-hidden="true" />Send me a test</button>}
          <span className="drip-spacer" />
          <button type="button" className="button secondary" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="submit" className="button" disabled={busy}>{busy ? "Saving…" : "Save email"}</button>
        </div>
      </form>
      <EmailPreview drip={drip} step={s} index={index} blank={blank} onToggle={onToggleBlank} />
    </li>
  );
}

/** The email as a lead reads it, with sample details (or none, to check the fallbacks). */
function EmailPreview({ drip, step, index, blank, onToggle }: { drip: Drip; step: DripStep; index: number; blank: boolean; onToggle: () => void }) {
  const person = blank ? BLANK_CONTACT : SAMPLE_CONTACT;
  const subject = mergeFields(step.subject, person).replace(/\s+/g, " ").trim();
  const paragraphs = previewParagraphs(mergeFields(step.body, person));
  const address = drip.footer_address.trim();
  const from = drip.from_address ? (drip.from_name ? `${drip.from_name} <${drip.from_address}>` : drip.from_address) : drip.from_name;
  return (
    <aside className="drip-preview" aria-label={`Preview of email ${index + 1}`}>
      <dl className="drip-preview-head">
        <div><dt>From</dt><dd>{from}</dd></div>
        {drip.reply_to && <div><dt>Reply to</dt><dd>{drip.reply_to}</dd></div>}
        <div><dt>Subject</dt><dd>{subject || <span className="drip-missing">No subject yet</span>}</dd></div>
      </dl>
      <div className="drip-preview-body">
        {paragraphs.length ? paragraphs.map((lines, p) => (
          <p key={p}>{lines.map((segs, l) => (
            <span key={l}>{l > 0 && <br />}{segs.map((x, k) => (x.href ? <a key={k} href={x.href} target="_blank" rel="noreferrer">{x.text}</a> : <span key={k}>{x.text}</span>))}</span>
          ))}</p>
        )) : <p className="drip-faint">The message shows here as you write it.</p>}
        <div className="drip-preview-foot">
          {address ? address.split("\n").map((line, i) => <span key={i}>{line}</span>) : <span className="drip-missing">Add your mailing address in Edit drip.</span>}
          <span>Don't want these emails? <u>Unsubscribe</u>.</span>
        </div>
      </div>
      <div className="drip-preview-note">
        <span>{blank ? "Shown for someone with no name or company on file." : `Shown for ${SAMPLE_CONTACT.first_name} ${SAMPLE_CONTACT.last_name} at ${SAMPLE_CONTACT.company}.`}</span>
        <button type="button" onClick={onToggle}>{blank ? "Show with sample details" : "Show with no details"}</button>
      </div>
    </aside>
  );
}

/** Where a person is in the drip, in one line. */
function personLine(p: DripPerson, total: number, status: Drip["status"]) {
  if (p.status === "active") {
    if (status === "paused") return `Paused before email ${p.next_position + 1} of ${total}`;
    if (status === "draft") return `Email ${p.next_position + 1} of ${total} goes once the drip is live`;
    return p.next_send_at ? `Email ${p.next_position + 1} of ${total} on ${shortWhen(p.next_send_at)}` : `Email ${p.next_position + 1} of ${total} is next`;
  }
  if (p.status === "finished") return `Finished: all ${total} emails sent`;
  if (p.status === "unsubscribed") return "Unsubscribed";
  return `${p.status === "failed" ? "Did not send" : "Stopped"}${p.stop_reason ? `: ${p.stop_reason}` : ""}`;
}

const PAGE = 25;

/** The people in the drip as rows, like Magus's prospects: Add people, Stop, Contact details. */
function DripPeople({ dripId, status, total, canWrite, onChanged }: { dripId: number; status: Drip["status"]; total: number; canWrite: boolean; onChanged: () => void }) {
  const people = useApi<{ steps: number; people: DripPerson[] }>(`/api/crm/drips/${dripId}/people`);
  const [params, setParams] = useSearchParams();
  const contactId = Number(params.get("contact")) || null;
  const openContact = (cid: number | null) => setParams((cur) => { const n = new URLSearchParams(cur); if (cid) n.set("contact", String(cid)); else n.delete("contact"); return n; });
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [shown, setShown] = useState(PAGE);
  const rows = people.data?.people || [];
  const activeIds = useMemo(() => new Set(rows.filter((r) => r.status === "active").map((r) => r.contact_id)), [rows]);

  async function stop(p: DripPerson) {
    setBusy(p.id); setNotice(null);
    try { await api(`/api/crm/drips/${dripId}/people/${p.id}/stop`, { method: "POST" }); people.reload(); onChanged(); }
    catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : "That did not work." }); }
    finally { setBusy(null); }
  }

  return (
    <>
      <div className="drip-section-heading">
        <h2>People</h2>
        {canWrite && <button className="button secondary" onClick={() => setAdding(true)}><Plus aria-hidden="true" />Add people</button>}
      </div>
      {notice && <p className={`inline-notice${notice.ok ? " success" : " danger"}`} role={notice.ok ? "status" : "alert"}>{notice.text}</p>}
      {people.loading && !people.data ? <LoadingState label="Loading people" />
        : people.error ? <ErrorState message={people.error} onRetry={people.reload} />
        : !rows.length ? (
          <div className="drip-empty is-small">
            <h2>Nobody in this drip yet</h2>
            <p>People join from the rules in Edit drip, or add them yourself.</p>
            {canWrite && <button className="button" onClick={() => setAdding(true)}>Add people</button>}
          </div>
        ) : (
          <div className="drip-people">
            {rows.slice(0, shown).map((p) => (
              <article key={p.id} className={`drip-person is-${p.status}`}>
                <div className="drip-person-main">
                  <h3>{contactName(p) || p.email}</h3>
                  <p>{[p.company, p.email].filter(Boolean).join(", ")}</p>
                  <p className="drip-person-line">{personLine(p, total, status)}</p>
                </div>
                <div className="drip-row-actions">
                  {canWrite && p.status === "active" && <button type="button" className="button secondary" disabled={busy === p.id} onClick={() => void stop(p)}>Stop</button>}
                  <button type="button" className="button secondary" onClick={() => openContact(p.contact_id)}>Contact details</button>
                </div>
              </article>
            ))}
            {rows.length > shown && <button type="button" className="button secondary drip-more-rows" onClick={() => setShown((n) => n + PAGE)}>Show {Math.min(PAGE, rows.length - shown)} more</button>}
          </div>
        )}
      {adding && (
        <AddPeopleDialog dripId={dripId} exclude={activeIds} onClose={() => setAdding(false)}
          onAdded={(text) => { setAdding(false); setNotice({ ok: true, text }); people.reload(); onChanged(); }} />
      )}
      {contactId && <ContactDrawer key={contactId} id={contactId} onClose={() => openContact(null)} onChanged={people.reload} />}
    </>
  );
}

/** Pick contacts to add, Magus's prospect picker: search, tick, add. */
function AddPeopleDialog({ dripId, exclude, onClose, onAdded }: { dripId: number; exclude: Set<number>; onClose: () => void; onAdded: (text: string) => void }) {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { const t = window.setTimeout(() => setTerm(q.trim()), 250); return () => window.clearTimeout(t); }, [q]);
  const contacts = useApi<ContactRow[]>(`/api/crm/contacts${term ? `?q=${encodeURIComponent(term)}` : ""}`);
  const list = (contacts.data || []).filter((c) => !exclude.has(c.id) && c.email);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!selected.length || busy) return;
    setBusy(true); setError("");
    try {
      const out = await api<{ added: number; already: number; skipped: { reason: string }[] }>(`/api/crm/drips/${dripId}/people`, { method: "POST", body: JSON.stringify({ contact_ids: selected }) });
      const parts = [`${out.added} ${out.added === 1 ? "person" : "people"} added`];
      if (out.already) parts.push(`${out.already} already in it`);
      if (out.skipped.length) parts.push(`${out.skipped.length} skipped (${out.skipped[0].reason.replace(/\.$/, "")})`);
      onAdded(`${parts.join(", ")}.`);
    } catch (err) { setError(err instanceof Error ? err.message : "That did not work."); setBusy(false); }
  }

  return (
    <Dialog title="Add people" onClose={() => { if (!busy) onClose(); }}>
      <form className="drip-form" onSubmit={(e) => void submit(e)}>
        <fieldset className="drip-form-set">
          <legend>People · {selected.length} selected</legend>
          <input type="search" aria-label="Search contacts" placeholder="Search by name, company or email" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="drip-picker">
            {contacts.loading && !contacts.data ? <p className="drip-faint" role="status">Loading contacts…</p>
              : contacts.error ? <p className="crm-error" role="alert">{contacts.error}</p>
              : !list.length ? <p className="drip-faint">{term ? "No contacts with an email match this search." : "No contacts with an email to add yet."}</p>
              : list.map((c) => (
                <label key={c.id}>
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={(e) => setSelected((cur) => (e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id)))} />
                  <span>{contactName(c) || c.email}<small>{[c.company, c.email].filter(Boolean).join(", ")}</small></span>
                </label>
              ))}
          </div>
        </fieldset>
        <p className="drip-form-hint">They start at email 1. Anyone who unsubscribed is skipped.</p>
        {error && <p className="crm-error" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="submit" className="button" disabled={busy || !selected.length}>{busy ? "Adding…" : selected.length ? `Add ${selected.length} ${selected.length === 1 ? "person" : "people"}` : "Add people"}</button>
        </div>
      </form>
    </Dialog>
  );
}

